import React, { useState, useEffect } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";
import { fastCatPreviewMetrics } from "@eagerdog/scoring-core";

import Button from "../../../components/Button/Button";
import DatePick from "../../../components/DatePick/DatePick";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/TextArea/TextArea";

import { formatRunDate, parseRunDate } from "./runFormFields";

import "./FastCATRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const FastCATRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [timeSeconds, setTimeSeconds] = useState<string>(editRun?.time_seconds?.toString() || "");
  const [heightInches, setHeightInches] = useState<string>(
    dog.height_at_withers?.toString() || ""
  );
  const [eventName, setEventName] = useState<string>(editRun?.event_name || "");
  const [city, setCity] = useState<string>(editRun?.location?.city || "");
  const [state, setState] = useState<string>(editRun?.location?.state || "");
  const [notes, setNotes] = useState<string>(editRun?.notes || "");

  // Calculated values
  const [mph, setMph] = useState<number>(0);
  const [heightClass, setHeightClass] = useState<string>("");
  const [handicapMultiplier, setHandicapMultiplier] = useState<number>(0);
  const [pointsEarned, setPointsEarned] = useState<number>(0);

  // Validation errors
  const [errors, setErrors] = useState<any>({});

  // Reset form when editRun changes (including when it becomes undefined for new entry)
  useEffect(() => {
    if (editRun) {
      // Editing existing run
      setTrialDate(editRun.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
      setTimeSeconds(editRun.time_seconds?.toString() || "");
      setHeightInches(dog.height_at_withers?.toString() || "");
      setEventName(editRun.event_name || "");
      setCity(editRun.location?.city || "");
      setState(editRun.location?.state || "");
      setNotes(editRun.notes || "");
    } else {
      // New entry - reset to defaults
      const defaultDate = new Date().toISOString().split('T')[0];
      setTrialDate(defaultDate);
      setTimeSeconds("");
      setHeightInches(dog.height_at_withers?.toString() || "");
      setEventName("");
      setCity("");
      setState("");
      setNotes("");
    }
    setErrors({});
  }, [editRun, dog.height_at_withers]);

  // Calculate Fast CAT metrics with the shared implementation (same math the API stores)
  useEffect(() => {
    const metrics = fastCatPreviewMetrics(timeSeconds, heightInches);
    if (metrics) {
      setMph(metrics.mph);
      setHeightClass(metrics.height_class);
      setHandicapMultiplier(metrics.handicap_multiplier);
      setPointsEarned(metrics.points_earned);
    } else {
      setMph(0);
      setHeightClass("");
      setHandicapMultiplier(0);
      setPointsEarned(0);
    }
  }, [timeSeconds, heightInches]);

  const validate = () => {
    const newErrors: any = {};

    if (!trialDate) {
      newErrors.trialDate = "Trial date is required";
    } else if (new Date(trialDate) > new Date()) {
      newErrors.trialDate = "Trial date cannot be in the future";
    }

    if (!timeSeconds || parseFloat(timeSeconds) <= 0) {
      newErrors.timeSeconds = "Valid run time is required";
    }

    if (!heightInches || parseFloat(heightInches) < 1 || parseFloat(heightInches) > 40) {
      newErrors.heightInches = "Height must be between 1 and 40 inches";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);

    const formData: any = {
      dog_id: dog._id,
      time_seconds: parseFloat(timeSeconds),
      height_at_withers: parseFloat(heightInches),
      trial_date: localDate,
      event_name: eventName || undefined,
      location: (city || state) ? {
        city: city || undefined,
        state: state || undefined
      } : undefined,
      notes: notes || undefined
    };

    // If editing, include the run ID
    if (editRun) {
      formData.run_id = editRun._id;
    }

    onSubmit(formData);
  };

  const heightWarning = dog.height_at_withers && 
    parseFloat(heightInches) > 0 && 
    Math.abs(dog.height_at_withers - parseFloat(heightInches)) > 0.5;

  return (
    <form className="fastCATRunForm" onSubmit={handleSubmit}>
      <div className="formSection">
        <h3>Run Details</h3>
        
        <div className="formGroup">
          <DatePick
            label="Trial Date *"
            placeholder="Select date"
            value={parseRunDate(trialDate)}
            maxDate={new Date()}
            onChange={(date: Date | null) => setTrialDate(formatRunDate(date))}
          />
          {errors.trialDate && <div className="fieldError">{errors.trialDate}</div>}
        </div>

        <div className="formGroup">
          <Input
            label="Run Time (seconds) *"
            id="timeSeconds"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="14.50"
            value={timeSeconds}
            onChange={(e) => setTimeSeconds(e.target.value)}
            required
          />
          {errors.timeSeconds && <div className="fieldError">{errors.timeSeconds}</div>}
        </div>

        <div className="formGroup">
          <Input
            label="Height at Withers (inches) *"
            id="heightInches"
            type="number"
            step="0.25"
            min="1"
            max="40"
            placeholder="18.5"
            value={heightInches}
            onChange={(e) => setHeightInches(e.target.value)}
            required
          />
          {errors.heightInches && <div className="fieldError">{errors.heightInches}</div>}
        </div>

        {heightWarning && (
          <div className="heightWarning">
            ⚠️ This height differs from your dog's profile ({dog.height_at_withers}")
          </div>
        )}
      </div>

      {/* Live calculation preview */}
      {mph > 0 && (
        <div className="calculationPreview">
          <h3>Calculated Results</h3>
          <div className="previewGrid">
            <div className="previewItem">
              <span className="label">Speed:</span>
              <span className="value">{mph.toFixed(2)} mph</span>
            </div>
            <div className="previewItem">
              <span className="label">Height Class:</span>
              <span className="value">{heightClass}"</span>
            </div>
            <div className="previewItem">
              <span className="label">Handicap:</span>
              <span className="value">{handicapMultiplier}x</span>
            </div>
            <div className="previewItem highlight">
              <span className="label">Points Earned:</span>
              <span className="value">{pointsEarned.toFixed(2)} pts</span>
            </div>
          </div>
          <div className="previewConfirmation">
            This will add <strong>{pointsEarned.toFixed(2)} points</strong> to your dog's total.
          </div>
        </div>
      )}

      <div className="formSection">
        <h3>Event Information (Optional)</h3>
        
        <div className="formGroup">
          <Input
            label="Event Name"
            id="eventName"
            type="text"
            placeholder="e.g., Spring Fast CAT Trial"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
          />
        </div>

        <div className="locationFields">
          <div className="formGroup">
            <Input
              label="City"
              id="city"
              type="text"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          
          <div className="formGroup">
            <Input
              label="State"
              id="state"
              type="text"
              placeholder="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength={2}
            />
          </div>
        </div>

        <div className="formGroup">
          <TextArea
            className="runNotes"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            placeholder="Any additional notes about this run..."
          />
          <div className="charCount">{notes.length}/500</div>
        </div>
      </div>

      <div className="modalActions">
        <Button type="button" onClick={onCancel} disabled={isSubmitting} variant="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || mph === 0}>
          {isSubmitting ? "Saving..." : editRun ? "Update Run" : "Add Run"}
        </Button>
      </div>
    </form>
  );
};

export default FastCATRunForm;

